import Loading from 'components/Loading';
import WithoutInvoice from 'components/WithoutInvoice';
import { InvoicePurchases } from 'constants/invoice';
import { format } from 'date-fns-tz';
import { pt } from 'date-fns/locale';
import { useBottomSheet } from 'hooks/bottomSheet';
import { useInvoice } from 'hooks/invoice';
import { useFetch } from 'hooks/useFetch';
import React, { useEffect, useState } from 'react';
import { FlatList, TouchableOpacity, View } from 'react-native';
import { PriceFormat } from 'services/price';
import shortid from 'shortid';

import { useNavigation } from '@react-navigation/native';

import InvoicePostingBottomSheet from './InvoicePostingBottomSheet';
import {
  CustomReload,
  Footer,
  FooterText,
  Header,
  HeaderSubtitle,
  HeaderTitle,
  HeaderTitleWidth,
  HeaderTitleText,
  InvoicePostingContainer,
  ListItem,
  ListItemContainer,
  ListItemIcon
} from './styles';

interface InvoicePostingProps {
  showAllButton?: boolean;
  title?: string;
  data?: InvoicePurchases[];
  showAllPurchases?: boolean;
}

const InvoicePosting: React.FC<InvoicePostingProps> = ({
  showAllButton = false,
  title,
  data,
  showAllPurchases = false
}) => {
  const navigation = useNavigation();
  const { openBottomSheet } = useBottomSheet();
  const { invoice } = useInvoice();
  const [purchases, setPurchases] = useState<InvoicePurchases[] | undefined>(
    data
  );

  const { data: allPurchases, error, mutate } = useFetch<InvoicePurchases[]>(
    () => {
      return showAllPurchases && invoice?.id
        ? `invoices/${invoice?.id}/purchases`
        : '';
    }
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [invoicePostings, setInvoicePostings] = useState<
    InvoicePurchases[] | undefined
  >(data);

  useEffect(() => {
    setPurchases(allPurchases);
  }, [allPurchases]);

  useEffect(() => {
    if (!showAllPurchases) {
      setPurchases(data);
    }
  }, [data]);

  useEffect(() => {
    let updatedTopPurchases: InvoicePurchases[] = [];
    if (purchases && purchases?.length > 0) {
      updatedTopPurchases = purchases.map(item => ({
        ...item,
        dateString: format(new Date(item.date), "dd 'de' MMM yyyy", {
          locale: pt
        }).toLowerCase(),
        isChargeBack: item.priceReais < 0
      }));
    }

    setInvoicePostings(updatedTopPurchases);

    if (purchases) {
      setLoading(false);
    }
  }, [purchases]);

  if (!loading && invoicePostings?.length === 0) {
    return <WithoutInvoice />;
  }

  return (
    <>
      <CustomReload
        onPress={() => mutate()}
        loaded={Array.isArray(invoicePostings) || !error}
        message="Não foi possível carregar seus lançamentos"
      >
        <Loading isLoading={loading || error} showSpinner={false}>
          <InvoicePostingContainer title={title} iconName="posting">
            <FlatList
              data={invoicePostings}
              renderItem={({ item, index }) => (
                <>
                  <TouchableOpacity
                    onPress={() =>
                      openBottomSheet(InvoicePostingBottomSheet, { data: item })
                    }
                  >
                    <ListItem>
                      <ListItemContainer>
                        <Header>
                          {item.howManyInstallments === 1 && (
                            <>
                              <HeaderTitle>
                                <HeaderTitleText
                                  ellipsizeMode="tail"
                                  numberOfLines={1}
                                >
                                  {item.description}
                                </HeaderTitleText>
                              </HeaderTitle>
                              {item.isChargeBack && (
                                <HeaderSubtitle
                                  isChargeBack={item.isChargeBack}
                                >
                                  (-) R$
                                  {PriceFormat(item.priceReais)}
                                </HeaderSubtitle>
                              )}
                              {!item.isChargeBack && (
                                <HeaderSubtitle
                                  isChargeBack={item.isChargeBack}
                                >
                                  R$
                                  {PriceFormat(item.priceReais)}
                                </HeaderSubtitle>
                              )}
                            </>
                          )}
                          {item.howManyInstallments > 1 && (
                            <>
                              <HeaderTitleWidth width={110}>
                                <HeaderTitleText
                                  ellipsizeMode="tail"
                                  numberOfLines={1}
                                >
                                  {item.description}
                                </HeaderTitleText>
                              </HeaderTitleWidth>
                              <View
                                style={{
                                  justifyContent: 'space-between',
                                  flexDirection: 'row',
                                  flex: 1
                                }}
                              >
                                <HeaderTitleText>
                                  {item.currentInstallment}/
                                  {item.howManyInstallments}
                                </HeaderTitleText>
                                {item.isChargeBack && (
                                  <HeaderSubtitle
                                    isChargeBack={item.isChargeBack}
                                  >
                                    (-) R$
                                    {PriceFormat(item.priceReais)}
                                  </HeaderSubtitle>
                                )}
                                {!item.isChargeBack && (
                                  <HeaderSubtitle
                                    isChargeBack={item.isChargeBack}
                                  >
                                    R$
                                    {PriceFormat(item.priceReais)}
                                  </HeaderSubtitle>
                                )}
                              </View>
                            </>
                          )}
                        </Header>
                        <Footer>
                          <FooterText>{item.dateString}</FooterText>
                          <FooterText>
                            Compra{' '}
                            {item.origin === 'NATIONAL'
                              ? 'nacional'
                              : 'internacional'}
                          </FooterText>
                        </Footer>
                      </ListItemContainer>
                      <ListItemIcon name="ios-arrow-forward" size={20} />
                    </ListItem>
                  </TouchableOpacity>
                  {showAllButton && invoicePostings?.length === index + 1 && (
                    <TouchableOpacity
                      testID="touchableInvoice"
                      onPress={() => navigation.navigate('PostingInvoices')}
                    >
                      <ListItem>
                        <ListItemContainer>
                          <Header>
                            <HeaderTitle>
                              <HeaderTitleText>
                                Ver todos os lançamentos
                              </HeaderTitleText>
                            </HeaderTitle>
                          </Header>
                        </ListItemContainer>
                        <ListItemIcon name="ios-arrow-forward" size={20} />
                      </ListItem>
                    </TouchableOpacity>
                  )}
                </>
              )}
              keyExtractor={shortid.generate}
            />
          </InvoicePostingContainer>
        </Loading>
      </CustomReload>
    </>
  );
};

export default InvoicePosting;
